import {
  SettingHeader,
  SettingWrapper,
} from '@affine/component/setting-components';
import {
  SubscriptionService,
  WorkspaceSubscriptionService,
} from '@affine/core/modules/cloud';
import { SubscriptionStatus } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect } from 'react';

import type { SettingState } from '../../types';
import { BillingHistory } from './billing-history';
import { PaymentMethod } from './payment-method';
import { ProPlanCard } from './pro-plan-card';
import * as styles from './style.css';
import { TypeformLink } from './typeform-link';

export const BillingSettings = ({
  onChangeSettingState,
}: {
  onChangeSettingState: (state: SettingState) => void;
}) => {
  const t = useI18n();

  return (
    <>
      <SettingHeader
        title={t['com.affine.payment.billing-setting.title']()}
        subtitle={t['com.affine.payment.billing-setting.subtitle']()}
      />
      <SettingWrapper
        title={t['com.affine.payment.billing-setting.information']()}
      >
        <SubscriptionSettings onChangeSettingState={onChangeSettingState} />
      </SettingWrapper>
      <SettingWrapper title={t['com.affine.payment.billing-setting.history']()}>
        <BillingHistory />
      </SettingWrapper>
    </>
  );
};

const SubscriptionSettings = ({
  onChangeSettingState,
}: {
  onChangeSettingState: (state: SettingState) => void;
}) => {
  const subscriptionService = useService(SubscriptionService);
  const workspaceSubscriptionService = useService(WorkspaceSubscriptionService);
  useEffect(() => {
    subscriptionService.subscription.revalidate();
    subscriptionService.prices.revalidate();
    workspaceSubscriptionService.subscription.revalidate();
  }, [subscriptionService, workspaceSubscriptionService]);

  const proSubscription = useLiveData(subscriptionService.subscription.pro$);
  const workspaceSub = useLiveData(
    workspaceSubscriptionService.subscription.subscription$
  );
  // Display whichever active subscription exists — workspace-level (Lab)
  // takes precedence over user-level (Researcher) when both are present.
  const activeSubscription = workspaceSub ?? proSubscription;

  const gotoCloudPlansSetting = useCallback(() => {
    track.$.settingsPanel.billing.viewPlans();
    onChangeSettingState({
      activeTab: 'plans',
      scrollAnchor: 'cloudPricingPlan',
    });
  }, [onChangeSettingState]);

  const hasActivePlan =
    activeSubscription !== null &&
    activeSubscription !== undefined &&
    activeSubscription.status === SubscriptionStatus.Active;

  // Single-card layout: ProPlanCard handles both the "no active plan"
  // and "active plan" states. AIPlanCard was AffineOS-specific and is
  // dropped — Curiescious doesn't sell AI as a separate add-on.
  return (
    <div className={styles.subscription}>
      <ProPlanCard gotoCloudPlansSetting={gotoCloudPlansSetting} />

      <TypeformLink />

      {hasActivePlan ? <PaymentMethod /> : null}
    </div>
  );
};

